import * as React from 'react';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from '@inertiajs/react';
import { ArrowRight } from 'lucide-react';

interface SliderProps {
  title: string;
  shortTitle?: string;
  icon?: React.ReactNode;
  viewAllLink?: string;
  viewAllText?: string;
  children: React.ReactNode;
}

export function Slider({
  title,
  shortTitle,
  icon,
  viewAllLink,
  viewAllText = 'Ver todos',
  children
}: SliderProps): React.ReactElement {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollButtons = useCallback(() => {
    if (!trackRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
    const canLeft = scrollLeft > 10;
    const canRight = scrollLeft < scrollWidth - clientWidth - 10;

    // Solo actualizar si cambió (evita re-renders innecesarios)
    setCanScrollLeft(prev => prev !== canLeft ? canLeft : prev);
    setCanScrollRight(prev => prev !== canRight ? canRight : prev);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateScrollButtons();

    // Throttle con requestAnimationFrame: máximo 1 llamada por frame
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateScrollButtons();
        ticking = false;
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons]);

  const scroll = (direction: 'left' | 'right') => {
    if (!trackRef.current) return;
    const scrollAmount = trackRef.current.clientWidth * 0.8;
    trackRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <section className="slider-section">
      <style>{`
        .slider-section {
          padding: 40px 0;
          background: #fff;
        }

        .slider-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .slider-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .slider-title {
          font-size: 24px;
          font-weight: 700;
          color: #333;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .slider-title-icon {
          display: flex;
          align-items: center;
          color: #5cb85c;
        }

        .slider-title-short {
          display: none;
        }

        .slider-view-all {
          font-size: 13px;
          font-weight: 600;
          color: #5cb85c;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: 1.5px solid #5cb85c;
          border-radius: 8px;
          background: transparent;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .slider-view-all:hover {
          background: #5cb85c;
          color: #fff;
          text-decoration: none;
        }

        .slider-wrapper {
          position: relative;
        }

        .slider-track {
          display: flex;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          gap: 16px;
          padding: 4px 8px;
          margin: 0 -8px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .slider-track::-webkit-scrollbar {
          display: none;
        }

        .slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          z-index: 2;
          transition: all 0.2s;
          color: #333;
          font-size: 16px;
        }

        .slider-arrow:hover:not(:disabled) {
          background: #5cb85c;
          border-color: #5cb85c;
          color: #fff;
        }

        .slider-arrow:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .slider-arrow-left {
          left: -22px;
        }

        .slider-arrow-right {
          right: -22px;
        }

        /* Mobile styles */
        @media (max-width: 768px) {
          .slider-section {
            padding: 24px 0;
          }

          .slider-title {
            font-size: 20px;
          }

          .slider-title-full {
            display: none;
          }

          .slider-title-short {
            display: inline;
          }

          .slider-view-all {
            font-size: 12px;
            padding: 6px 12px;
          }

          .slider-arrow {
            display: none;
          }

          .slider-track {
            gap: 12px;
            padding: 4px 8px;
          }
        }
      `}</style>

      <div className="slider-container">
        <div className="slider-header">
          <h2 className="slider-title">
            {icon && <span className="slider-title-icon">{icon}</span>}
            {shortTitle ? (
              <>
                <span className="slider-title-full">{title}</span>
                <span className="slider-title-short">{shortTitle}</span>
              </>
            ) : title}
          </h2>
          {viewAllLink && (
            <Link href={viewAllLink} className="slider-view-all">
              {viewAllText}
              <ArrowRight size={16} />
            </Link>
          )}
        </div>

        <div className="slider-wrapper">
          <button
            className="slider-arrow slider-arrow-left"
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            aria-label="Anterior"
          >
            <i className="fa fa-chevron-left"></i>
          </button>

          <div className="slider-track" ref={trackRef}>
            {children}
          </div>

          <button
            className="slider-arrow slider-arrow-right"
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            aria-label="Siguiente"
          >
            <i className="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
